// OwlSystemTrackRecord — the system-level OWL HOUR track record panel.
//
// Per founder directive (OWL-U1): "keep ready of previous OWL HOUR
// games and their end results". This is distinct from OwlHistory which
// shows the user's own locked slips. This panel shows what the SYSTEM
// has been emitting + how it settled.
//
// Reads via useOwlSystemTrackRecord. Honest-empty if no settled slips.
// Public-communication rules: never expose per-league win-rate or
// methodology; ROI/sample-size only.

import { useState } from "react";
import { useOwlSystemTrackRecord, type OwlSystemSlip } from "../../owl/useOwlSystemTrackRecord.ts";
import { OWL_TIER_META, type OwlTier } from "../../owl/useOwlHourSlips.ts";

const TIER_ORDER: OwlTier[] = ["rollover", "moderate", "conservative", "aggressive", "owl_1000"];

function fmtDate(raw: string): string {
  if (!raw) return "";
  const d = new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return raw;
  const day = d.getUTCDate().toString().padStart(2, "0");
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${day} ${mon}`;
}

function resultStyle(r: OwlSystemSlip["result"]): { bg: string; fg: string; label: string } {
  switch (r) {
    case "win":     return { bg: "rgba(154, 175, 158, 0.12)", fg: "#9aaf9e", label: "WON" };
    case "loss":    return { bg: "rgba(220, 80, 80, 0.15)",  fg: "#e58a8a", label: "LOST" };
    case "partial": return { bg: "rgba(184, 165, 118, 0.12)", fg: "#b8a576", label: "PART" };
    case "void":    return { bg: "rgba(140, 140, 140, 0.15)", fg: "#b0b0b0", label: "VOID" };
    default:        return { bg: "rgba(110, 103, 136, 0.10)", fg: "var(--owl-text-3)", label: "PENDING" };
  }
}

export function OwlSystemTrackRecord(): JSX.Element {
  const { slips, summary, loading, error } = useOwlSystemTrackRecord(30);
  const [tierFilter, setTierFilter] = useState<OwlTier | "all">("all");

  // Group by night
  const byNight = new Map<string, OwlSystemSlip[]>();
  for (const s of slips) {
    if (tierFilter !== "all" && s.tier !== tierFilter) continue;
    const key = `${s.slip_date}|${s.refresh_phase ?? "evening"}`;
    const bucket = byNight.get(key);
    if (bucket) bucket.push(s);
    else byNight.set(key, [s]);
  }
  const nights = [...byNight.entries()];

  return (
    <div className="owl-track-record" style={{
      background: "var(--owl-elev)",
      border: "1px solid var(--owl-border)",
      borderRadius: 14,
      padding: 0,
      marginTop: 24,
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "16px 20px",
        borderBottom: "1px solid var(--owl-border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em" }}>
            OWL HOUR · Track record
          </div>
          <div style={{ fontSize: 11, color: "var(--owl-text-3)", marginTop: 2 }}>
            Every night the system has emitted in the last 30 days. ROI over a meaningful sample is the honest measure.
          </div>
        </div>

        {/* Summary chips */}
        {summary.total_slips > 0 ? (
          <div style={{ display: "flex", gap: 10, fontSize: 11, fontFamily: "var(--font-mono)" }}>
            <Chip label="Nights" value={String(summary.total_nights)} />
            <Chip label="Slips" value={String(summary.total_slips)} />
            <Chip label="Unit ROI" value={summary.unit_roi === null ? "Building sample" : `${(summary.unit_roi * 100).toFixed(1)}%`} />
            <Chip label="Settled sample" value={String(summary.roi_sample)} />
            {summary.partials > 0 ? <Chip label="Partial" value={String(summary.partials)} /> : null}
            {summary.pending > 0 ? <Chip label="Pending" value={String(summary.pending)} /> : null}
          </div>
        ) : null}

        {/* Tier filter */}
        <div style={{ display: "flex", gap: 4, fontSize: 11 }}>
          {(["all", ...TIER_ORDER] as Array<OwlTier | "all">).map((t) => {
            const active = tierFilter === t;
            const label = t === "all" ? "All" : (OWL_TIER_META[t]?.label.split(" ")[0] ?? t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTierFilter(t)}
                style={{
                  background: active ? "var(--owl-violet)" : "transparent",
                  color: active ? "white" : "var(--owl-text-2)",
                  border: "1px solid var(--owl-border)",
                  borderRadius: 6,
                  padding: "4px 10px",
                  cursor: "pointer",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--owl-text-3)", fontSize: 12 }}>
          Loading track record…
        </div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: "center", color: "#e58a8a", fontSize: 12 }}>
          Couldn't load the track record. {error}
        </div>
      ) : nights.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--owl-text-3)", fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>🌙</div>
          No OWL HOUR slips emitted yet in the last 30 days.
          <div style={{ fontSize: 11, marginTop: 6 }}>
            Published tickets and confirmed results appear here.
          </div>
        </div>
      ) : (
        <div>
          {nights.map(([date, daySlips]) => (
            <NightRow key={date} date={date.split("|")[0]!} slips={daySlips} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, accent }: { label: string; value: string; accent?: "pos" | "neg" }): JSX.Element {
  const color = accent === "pos" ? "#9aaf9e" : accent === "neg" ? "#e58a8a" : "var(--owl-text-2)";
  return (
    <span style={{
      display: "inline-flex",
      gap: 4,
      alignItems: "baseline",
      padding: "3px 8px",
      border: "1px solid var(--owl-border)",
      borderRadius: 6,
    }}>
      <span style={{ color: "var(--owl-text-3)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </span>
      <span style={{ color, fontWeight: 700 }}>{value}</span>
    </span>
  );
}

function NightRow({ date, slips }: { date: string; slips: readonly OwlSystemSlip[] }): JSX.Element {
  const byTier = new Map<OwlTier, OwlSystemSlip>();
  for (const s of slips) byTier.set(s.tier, s);

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "90px repeat(5, minmax(110px, 1fr))",
      gap: 0,
      borderBottom: "1px solid var(--owl-border)",
      alignItems: "stretch",
    }}>
      <div style={{
        padding: "12px 14px",
        fontSize: 12,
        color: "var(--owl-text-2)",
        fontFamily: "var(--font-mono)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderRight: "1px solid var(--owl-border)",
      }}>
        <div style={{ fontWeight: 700, color: "var(--owl-text-1)" }}>{fmtDate(date)}</div>
        <div style={{ fontSize: 9, color: "var(--owl-text-3)", marginTop: 2 }}>{slips[0]?.refresh_phase === "noon" ? "Noon" : "Evening"} · Accra</div>
      </div>

      {TIER_ORDER.map((tier) => {
        const slip = byTier.get(tier);
        if (!slip) {
          return (
            <div key={tier} style={{
              padding: "12px 10px",
              borderRight: "1px solid var(--owl-border)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              fontSize: 10,
              color: "var(--owl-text-3)",
              fontStyle: "italic",
            }}>
              honest-empty
            </div>
          );
        }
        const r = resultStyle(slip.result);
        return (
          <div key={tier} style={{
            padding: "10px 12px",
            borderRight: "1px solid var(--owl-border)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}>
            <div style={{
              fontSize: 9,
              color: "var(--owl-text-3)",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}>
              {OWL_TIER_META[tier]?.label.split(" ")[0] ?? tier}
            </div>
            <div style={{
              fontSize: 13,
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              color: "var(--owl-text-1)",
            }}>
              {slip.combined_odds ? `${slip.combined_odds.toFixed(2)}×` : "0.00×"}
              <span style={{ fontSize: 10, color: "var(--owl-text-3)", marginLeft: 6, fontWeight: 400 }}>
                {slip.legs_count} legs
              </span>
            </div>
            <div style={{
              display: "inline-block",
              alignSelf: "flex-start",
              fontSize: 9,
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              letterSpacing: "0.06em",
              padding: "2px 6px",
              borderRadius: 4,
              background: r.bg,
              color: r.fg,
            }}>
              {r.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
